<template>
  <section class="social-proof" :class="{ desktop: $isDesktop }">
    <a :href="feefoLink" target="_blank" rel="external nofollow noopener">
      <img
        v-if="$isDesktop"
        src="@/assets/homepage/press/feefo-48-en.svg"
        loading="lazy"
        :alt="$t('Feefo service rating')"
        :title="$t('Visit Feefo - and see what our customers say about us')"
        width="284"
        height="69"
      />
      <img
        v-else
        src="@/assets/homepage/press/feefo-48-en-m.svg"
        loading="lazy"
        :alt="$t('Feefo service rating')"
        :title="$t('Visit Feefo - and see what our customers say about us')"
        width="200"
        height="50"
      />
    </a>
    <slot name="press">
      <div class="press">
        <img
          src="@/assets/homepage/press/the-times.svg"
          loading="lazy"
          alt="The Times"
          width="268"
          height="32"
          class="logo-the-times"
        />
        <img
          src="@/assets/homepage/press/bbc-news.svg"
          loading="lazy"
          alt="BBC News"
          width="52"
          height="40"
        />
        <img
          src="@/assets/homepage/press/vogue.svg"
          loading="lazy"
          alt="Vogue"
          width="139"
          height="36"
        />
        <template v-if="$isDesktop">
          <img
            src="@/assets/homepage/press/itv.svg"
            loading="lazy"
            alt="ITV"
            width="48"
            height="40"
          />
          <img
            src="@/assets/homepage/press/the-guardian.svg"
            loading="lazy"
            alt="The Guardian"
            width="94"
            height="40"
          />
          <img
            src="@/assets/homepage/press/sky.svg"
            loading="lazy"
            alt="Sky"
            width="51"
            height="40"
          />
        </template>
      </div>
    </slot>
  </section>
</template>

<script lang="ts">
import { Component, Vue, toNative } from '~/utility/pu-class-decorator'

@Component
class SocialProof extends Vue {
  get feefoLink() {
    if (this.$i18n.locale === 'fr') {
      return 'https://ww2.feefo.com/reviews/pitchup-france'
    } else if (this.$i18n.locale === 'it') {
      return 'https://ww2.feefo.com/reviews/pitchup-italy'
    } else if (this.$i18n.locale === 'es') {
      return 'https://ww2.feefo.com/reviews/pitchup-spain'
    } else {
      return 'https://ww2.feefo.com/reviews/pitchup-com'
    }
  }
}

export default toNative(SocialProof)
</script>

<style scoped>
section {
  display: flex;
  background: white;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 1.4em 1em;
}

a {
  border-right: 1px solid var(--colour-page-background);
  padding-right: 0.75em;
  margin-right: 0.75em;
}

img {
  width: auto;
}

a img {
  height: auto;
  min-width: 10.5em;
  padding: 0.5em 0;
}

:deep(.press) {
  display: grid;
  width: 100%;
  align-items: center;
  justify-items: center;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  column-gap: 0.5em;
}

:deep(.press img:first-of-type) {
  grid-row: 1 / 2;
  grid-column: 1 / 3;
  max-height: 1.2em;
  margin-bottom: 0.3em;
}

:deep(.press img) {
  opacity: 0.6;
  height: 1.8em;
  filter: grayscale(1);
}

/* mobile landscape */
@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  :deep(.press) {
    grid-template-columns: repeat(3, auto);
    grid-template-rows: auto;
  }

  :deep(.press img:first-of-type) {
    grid-row: unset;
    grid-column: unset;
  }
}

/* desktop & tablet */
.desktop section {
  background: #f4f6f8;
  padding: 3em 5em;
  column-gap: 3em;
  margin-top: 0;
}

.desktop a {
  display: flex;
  background: white;
  border-radius: 0.65em;
  justify-content: center;
  align-items: center;
  padding: 0.8em 1.2em;
  flex-shrink: 0;
  border: none;
}

.desktop :deep(.press) {
  grid-template-columns: repeat(3, minmax(3em, auto));
  grid-template-rows: auto;
  row-gap: 0.75em;
  column-gap: 1.5em;
}

.desktop :deep(.press) img {
  height: auto;
  min-height: 1.8em;
  max-height: 2.5em;
}

.desktop .logo-the-times {
  min-width: 10em;
}

.desktop :deep(.press img:first-of-type) {
  grid-row: unset;
  grid-column: unset;
}

.desktop a img {
  height: 4.2em;
  padding: 0;
}

@media (max-width: 850px) {
  .desktop section {
    column-gap: 1.5em;
  }

  .desktop :deep(.press) {
    grid-template-columns: repeat(3, minmax(3em, auto));
    column-gap: 1em;
  }

  .desktop .logo-the-times {
    min-width: 6em;
  }
}

@media (min-width: 1300px) {
  .desktop :deep(.press) {
    grid-template-columns: repeat(6, minmax(6em, auto));
    column-gap: 1.5em;
  }

  .desktop :deep(.press img) {
    max-height: 3.5em;
  }
}

@media (min-width: 1400px) {
  .desktop :deep(.press) {
    column-gap: 2em;
  }
}

@media (hover: hover) {
  a {
    transition: background-color 0.15s ease-out;
  }

  a:hover,
  a:focus-visible {
    background-color: var(--colour-page-background);
    outline: none;
  }

  a:hover img,
  a:focus-visible img {
    filter: brightness(0.75) grayscale(1);
  }
}
</style>
